import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Sparkles, Calendar, Clock, MapPin, Users, 
  Heart, Check, ArrowRight, Loader2, Bell, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RetreatPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [signedUp, setSignedUp] = useState(false);
  const { user } = useAuth();

  // Pre-fill if user is logged in
  React.useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/waitlist/signup`, {
        name,
        email,
        event_id: 'alignment-activation-retreat-2027'
      });
      
      setSignedUp(true);
      if (response.data.already_signed_up) {
        toast.info("You're already on the list!");
      } else {
        toast.success("You're on the list! We'll notify you when registration opens.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="retreat-page">
      {/* Hero Section */}
      <div className="relative rounded-3xl overflow-hidden">
        <img 
          src="https://static.showit.co/file/VAKicQdUFs84pzOHgfZLvw/160323/web_background-6.png"
          alt="Women at retreat"
          className="w-full h-64 md:h-80 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="inline-flex items-center gap-2 bg-accent/90 text-white px-4 py-2 rounded-full font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Coming January 2027
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
            Alignment Activation Retreat
          </h1>
          <p className="text-lg text-stone-200 max-w-2xl">
            A transformational one-day in-person experience for women ready to activate their next chapter
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* What to Expect */}
          <div className="card-cosmic p-8">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">What to Expect</h2>
            <p className="text-lg text-stone-600 mb-6">
              Join me for a full day of deep transformation, connection, and activation. 
              This intimate gathering is designed for women who are ready to step into alignment 
              with their truest selves.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Heart, text: 'Guided meditation & breathwork' },
                { icon: Users, text: 'Small intimate group (max 20)' },
                { icon: Sparkles, text: 'Transformational exercises' },
                { icon: Star, text: 'Connection & community' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-stone-700">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Event Details */}
          <div className="card-cosmic p-8">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">Event Details</h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">January 2027</p>
                  <p className="text-stone-500">Exact date to be announced</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">10:00 AM – 7:00 PM</p>
                  <p className="text-stone-500">Full day experience</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">Location TBA</p>
                  <p className="text-stone-500">In-person event (details coming soon)</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">Limited to 20 Women</p>
                  <p className="text-stone-500">Intimate, transformational experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Signup Sidebar */}
        <div className="lg:col-span-1">
          <div className="card-cosmic p-6 sticky top-6">
            {!signedUp ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-4">
                    <Bell className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Get Notified</h3>
                  <p className="text-stone-600 text-sm">
                    Be the first to know when registration opens. Spots are limited!
                  </p>
                </div>

                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-stone-700">Your Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      className="input-cosmic mt-1"
                      required
                      data-testid="waitlist-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-stone-700">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="input-cosmic mt-1"
                      required
                      data-testid="waitlist-email-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-cosmic"
                    disabled={loading}
                    data-testid="waitlist-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing up...
                      </>
                    ) : (
                      <>
                        Join the Waitlist
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-stone-500 text-center mt-4">
                  No spam, ever. Just retreat updates.
                </p>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-2">You're on the List!</h3>
                <p className="text-stone-600">
                  We'll send you an email as soon as registration opens. Get excited!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetreatPage;
